/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isSuperAdmin } from '@marapp/earth-shared';

import { useAuth0 } from '../../auth/auth0';
import { MAP_ADMIN_URL } from '../../config';
import AsyncPage from './async';
import AuthenticatedPage from './authenticated';
import AuthorizedPage from './authorized';

const Main = ({ router, isWebGL }) => {
  const {
    isLoading,
    isAuthenticated,
    isEmailVerified,
    userData,
    login,
  } = useAuth0();
  const { t } = useTranslation();

  const [sessionExpired, setSessionExpired] = useState(false);

  // Listen for hard session-expired events from the interceptor
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, []);
  const { pathname, type, routesMap } = router;
  const { page, isAuthRequired, isAuthzRequired, fallbackRouteResolver } = routesMap[type];
  const context = { pathname, isLoading, isEmailVerified, isAuthenticated };

  if (userData && userData.allGroups?.length === 0 && isSuperAdmin(userData.roles as any)) {
    window.location.assign(MAP_ADMIN_URL);
  }

  let PageComponent;
  switch (true) {
    case isAuthRequired && isAuthRequired(context) && isAuthzRequired(context):
      PageComponent = AuthorizedPage;
      break;
    case (isAuthRequired && isAuthRequired(context)) || (isAuthenticated && !isEmailVerified):
      PageComponent = AuthenticatedPage;
      break;
    default:
      PageComponent = AsyncPage;
  }

  let pageElement = null;
  switch (page) {
    case 'home':
      pageElement = (
        <PageComponent
          page="home"
          fallbackRoute={fallbackRouteResolver(context)}
          isLoading={isLoading}
          isWebGL={isWebGL}
        />
      );
      break;
    case 'earth':
      pageElement = <PageComponent page="earth" fallbackRoute={fallbackRouteResolver(context)} />;
      break;
    case 'elsa':
      pageElement = <PageComponent page="elsa" fallbackRoute={fallbackRouteResolver(context)} />;
      break;
    case 'experience':
      pageElement = (
        <PageComponent page="experience" fallbackRoute={fallbackRouteResolver(context)} />
      );
      break;
    case 'change-email':
      pageElement = (
        <PageComponent page="change-email" fallbackRoute={fallbackRouteResolver(context)} />
      );
      break;
    case 'profile':
      pageElement = <PageComponent page="profile" fallbackRoute={fallbackRouteResolver(context)} />;
      break;
    case 'not-found':
      pageElement = (
        <PageComponent page="not-found" fallbackRoute={fallbackRouteResolver(context)} />
      );
      break;
    case 'error':
      pageElement = <PageComponent page="error" fallbackRoute={fallbackRouteResolver(context)} />;
      break;
    case 'unauthorized':
      pageElement = (
        <PageComponent page="unauthorized" fallbackRoute={fallbackRouteResolver(context)} />
      );
      break;
    case 'verify-email':
      pageElement = <PageComponent page="verify-email" />;
      break;
  }

  return (
    <>
      {pageElement}

{/* Session fully expired — must log in again */}
      <Snackbar open={sessionExpired} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => login()}>
              {t('Log In')}
            </Button>
          }
        >
          {t('Your session has expired. Please log in again.')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Main;
